import { computed, ref } from "vue";
import { UserNotification } from "@/api/subscriptions";

const STORAGE_KEY = "procyon:desktop-notifications";
const MAX_BODY_CHANGES = 4;

const supported = typeof window !== "undefined" && "Notification" in window;

// Module-level state so the settings page and the subscriptions store share one instance.
const enabled = ref(supported && localStorage.getItem(STORAGE_KEY) === "true");
const permission = ref<NotificationPermission>(supported ? Notification.permission : "denied");

export function useDesktopNotifications() {
    const blocked = computed(() => supported && permission.value === "denied");
    const active = computed(() => enabled.value && permission.value === "granted");

    async function enable(): Promise<boolean> {
        if (!supported) return false;

        if (permission.value === "default") {
            permission.value = await Notification.requestPermission();
        }
        if (permission.value !== "granted") return false;

        enabled.value = true;
        localStorage.setItem(STORAGE_KEY, "true");
        return true;
    }

    function disable() {
        enabled.value = false;
        localStorage.setItem(STORAGE_KEY, "false");
    }

    /**
     * Shows a system notification for an incoming update, but only while the tab is in the
     * background; a focused tab already surfaces it through the in-app bell.
     */
    function showFor(notification: UserNotification) {
        if (!active.value || document.hasFocus()) return;

        const shown = new Notification(notification.title ?? "Procyon", {
            body: buildBody(notification),
            // Re-notifying the same subscription replaces its toast instead of stacking.
            tag: notification.id,
        });

        const linkPath = notification.linkPath;
        shown.onclick = () => {
            window.focus();
            shown.close();
            if (linkPath) {
                void import("@/router").then(({ default: router }) => {
                    void router.push(linkPath);
                });
            }
        };
    }

    return { supported, enabled, permission, blocked, active, enable, disable, showFor };
}

export function buildBody(notification: UserNotification): string {
    const latest = [...notification.changeLogs].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    )[0];
    if (!latest) return notification.message ?? "";

    const changes = latest.changes
        .slice(0, MAX_BODY_CHANGES)
        .map(
            (c) => `${c.label ?? c.field ?? "Field"}: ${c.from ?? "(none)"} → ${c.to ?? "(none)"}`
        );
    if (latest.changes.length > MAX_BODY_CHANGES) {
        changes.push(`and ${latest.changes.length - MAX_BODY_CHANGES} more`);
    }

    return [latest.displayName, ...changes].join("\n");
}
